import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FiSettings } from 'react-icons/fi';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut } from '@/lib/api';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { Skeleton } from '@/shared/components/ui/Skeleton';

interface MaskedCard { maskedNumber: string; brand: string; isDefault: boolean }
interface PlanLite { id: string; name: string; price: { monthly: number; yearly: number; currency: string }; creditsPerCycle: number }
interface SubInfo {
  subscription: {
    planId: string; cycle: 'monthly' | 'yearly';
    discount: { type: 'percent' | 'fixed'; value: number } | null;
    status: string; paymentMethod: string; defaultCardIndex: number; nextRenewalAt: string | null;
  } | null;
  cards: MaskedCard[];
  plans: PlanLite[];
}

interface SubscriptionDrawerProps {
  company: { id: string; name: string } | null;
  isOpen: boolean;
  onClose: () => void;
}

const formatCurrency = (amount: number, currency = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(amount);

// Mesma fórmula do helper backend (manter em sincronia)
const computeTotal = (price: number, dType: 'none' | 'percent' | 'fixed', dValue: number): { discountAmount: number; total: number } => {
  if (dType === 'none' || !dValue || dValue <= 0) return { discountAmount: 0, total: price };
  const raw = dType === 'percent' ? price * (Math.min(dValue, 100) / 100) : Math.min(dValue, price);
  const discountAmount = Math.round(raw * 100) / 100;
  return { discountAmount, total: Math.max(0, Math.round((price - discountAmount) * 100) / 100) };
};

export const SubscriptionDrawer: React.FC<SubscriptionDrawerProps> = ({ company, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const companyId = company?.id ?? null;

  const [planId, setPlanId] = useState('');
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [discountType, setDiscountType] = useState<'none' | 'percent' | 'fixed'>('none');
  const [discountValue, setDiscountValue] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'pix'>('credit_card');
  const [cardIndex, setCardIndex] = useState(0);
  const [status, setStatus] = useState('active');

  const { data: info, isLoading } = useQuery({
    queryKey: ['admin-company-subscription', companyId],
    queryFn: () => apiGet<SubInfo>(`/companies/${companyId}/subscription`),
    enabled: !!companyId && isOpen,
  });

  const isEdit = !!info?.subscription;

  useEffect(() => {
    if (!info) return;
    const s = info.subscription;
    if (s) {
      setPlanId(s.planId); setCycle(s.cycle);
      setDiscountType(s.discount ? s.discount.type : 'none');
      setDiscountValue(s.discount ? String(s.discount.value) : '');
      setPaymentMethod(s.paymentMethod === 'pix' ? 'pix' : 'credit_card');
      setCardIndex(s.defaultCardIndex ?? 0);
      setStatus(s.status);
    } else {
      setPlanId(info.plans[0]?.id ?? ''); setCycle('monthly');
      setDiscountType('none'); setDiscountValue(''); setPaymentMethod('credit_card');
      setCardIndex(info.cards.findIndex((c) => c.isDefault) >= 0 ? info.cards.findIndex((c) => c.isDefault) : 0);
      setStatus('active');
    }
  }, [info]);

  const plan = info?.plans.find((p) => p.id === planId);
  const price = plan ? (cycle === 'yearly' ? plan.price.yearly : plan.price.monthly) : 0;
  const currency = plan?.price.currency ?? 'BRL';
  const { total } = computeTotal(price, discountType, parseFloat(discountValue) || 0);
  const discountPayload = discountType === 'none' ? null : { type: discountType, value: parseFloat(discountValue) || 0 };

  const onDone = (msg: string) => {
    queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
    queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
    toast.success(msg);
    onClose();
  };

  const activateMutation = useMutation({
    mutationFn: (charge: boolean) => apiPost(`/companies/${companyId}/subscription/activate`, {
      planId, cycle, discount: discountPayload, paymentMethod, cardIndex, charge,
    }),
    onSuccess: () => onDone('Assinatura ativada'),
  });

  const updateMutation = useMutation({
    mutationFn: () => apiPut(`/companies/${companyId}/subscription`, {
      planId, cycle, discount: discountPayload, defaultCardIndex: cardIndex, status,
    }),
    onSuccess: () => onDone('Assinatura atualizada'),
  });

  const busy = activateMutation.isPending || updateMutation.isPending;
  const planOptions = (info?.plans ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.creditsPerCycle} créditos)` }));
  const cardOptions = (info?.cards ?? []).map((c, i) => ({ value: String(i), label: c.isDefault ? `${c.maskedNumber} · ${c.brand} — principal` : `${c.maskedNumber} · ${c.brand}` }));
  const needsCard = paymentMethod === 'credit_card';

  return (
    <Drawer
      isOpen={isOpen} onClose={onClose} size="md"
      title={isEdit ? 'Editar assinatura' : 'Ativar assinatura'}
      subtitle={company?.name}
      icon={<FiSettings className="w-5 h-5" />}
      disabled={busy}
      footer={
        isEdit ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
            <Button onClick={() => updateMutation.mutate()} loading={updateMutation.isPending}>Salvar</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => activateMutation.mutate(false)} loading={activateMutation.isPending && activateMutation.variables === false} disabled={busy || !planId}>Ativar sem cobrar</Button>
            <Button onClick={() => activateMutation.mutate(true)} loading={activateMutation.isPending && activateMutation.variables === true} disabled={busy || !planId || paymentMethod === 'pix' || (needsCard && cardOptions.length === 0)}>
              {`Ativar e cobrar ${formatCurrency(total, currency)}`}
            </Button>
          </>
        )
      }
    >
      {isLoading ? (
        <div className="space-y-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={40} />)}</div>
      ) : (
        <div className="space-y-4">
          <FormField label="Plano">
            <Select value={planId} onChange={setPlanId} options={planOptions} />
          </FormField>
          <FormField label="Ciclo">
            <Select value={cycle} onChange={(v) => setCycle(v as 'monthly' | 'yearly')} options={[{ value: 'monthly', label: 'Mensal' }, { value: 'yearly', label: 'Anual' }]} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Desconto">
              <Select value={discountType} onChange={(v) => setDiscountType(v as 'none' | 'percent' | 'fixed')} options={[{ value: 'none', label: 'Nenhum' }, { value: 'percent', label: 'Percentual (%)' }, { value: 'fixed', label: 'Valor fixo (R$)' }]} />
            </FormField>
            {discountType !== 'none' && (
              <FormField label={discountType === 'percent' ? '% de desconto' : 'R$ de desconto'}>
                <Input type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder="0" />
              </FormField>
            )}
          </div>
          <FormField label="Método de pagamento">
            <Select value={paymentMethod} onChange={(v) => setPaymentMethod(v as 'credit_card' | 'pix')} options={[{ value: 'credit_card', label: 'Cartão de crédito' }, { value: 'pix', label: 'PIX' }]} />
          </FormField>
          {needsCard && (
            <FormField label="Cartão">
              {cardOptions.length > 0
                ? <Select value={String(cardIndex)} onChange={(v) => setCardIndex(Number(v))} options={cardOptions} />
                : <p className="text-sm" style={{ color: '#dc2626' }}>Nenhum cartão cadastrado.</p>}
            </FormField>
          )}
          {isEdit && (
            <FormField label="Status">
              <Select value={status} onChange={setStatus} options={[
                { value: 'active', label: 'Ativa' }, { value: 'past_due', label: 'Inadimplente' },
                { value: 'suspended', label: 'Suspensa' }, { value: 'cancelled', label: 'Cancelada' },
              ]} />
            </FormField>
          )}
          {plan && (
            <div className="text-sm rounded-lg p-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', color: '#374151' }}>
              Preço: <b>{formatCurrency(price, currency)}</b>{discountType !== 'none' && (<> · Com desconto: <b>{formatCurrency(total, currency)}</b></>)}
            </div>
          )}
          {!isEdit && paymentMethod === 'pix' && (
            <p className="text-xs" style={{ color: '#94a3b8' }}>Cobrança imediata é só por cartão. Para PIX, use “Ativar sem cobrar” — as renovações seguintes são cobradas via PIX com o desconto.</p>
          )}
          {isEdit && (
            <p className="text-xs" style={{ color: '#94a3b8' }}>Para cobrar agora, use “Efetuar pagamento” no menu. Alterações aqui valem na próxima renovação.</p>
          )}
        </div>
      )}
    </Drawer>
  );
};
